import Stack from 'atoms/Layout/Stack/Stack';
import BaseModal from 'molecules/BaseModal/BaseModal';
import React, { useEffect } from 'react';
import { getLocalStorage, setLocalStorage } from 'src/functions/localStorageHandler';
import {
  QUIZ_IFRAME_INTERACTION_STORAGE_KEY,
  QUIZ_SKIPPING_INTRO_ID,
  QUIZ_WITH_INTRO_ID,
} from 'src/hooks/usePricingQuizModalController';
import { IFrame, IFrameContainer, QuizContainer } from './PricingQuizModal.styles';
import { trackEverflowConversion } from 'src/functions/everflow/everflow.service';

interface PricingQuizModalProps {
  visible: boolean;
  onClose: () => void;
  skipIntro: boolean;
}

const PricingQuizModal = ({ visible, onClose, skipIntro }: PricingQuizModalProps): JSX.Element => {
  const trackInteractionWithQuizIframe = (): void => {
    if (document.activeElement instanceof HTMLIFrameElement) {
      const hasInteracted = getLocalStorage(QUIZ_IFRAME_INTERACTION_STORAGE_KEY);

      if (hasInteracted === 'true') return;

      setLocalStorage(QUIZ_IFRAME_INTERACTION_STORAGE_KEY, 'true');
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.addEventListener('blur', trackInteractionWithQuizIframe);

    return () => {
      window.removeEventListener('blur', trackInteractionWithQuizIframe);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleQuizCompletion = (event: MessageEvent) => {
      // TryInteract sends postMessage events when quiz is completed
      if (event.origin !== 'https://quiz.tryinteract.com') return;

      // Check for quiz completion event
      if (event.data?.type === 'interact_quiz_complete' || event.data?.event === 'quiz_complete') {
        const email = event.data?.email || event.data?.result?.email;
        const quizResult = event.data?.result || event.data?.quiz_result;

        // Track Everflow conversion
        trackEverflowConversion({
          event_id: 'pricing_quiz',
          email: email,
          adv1: 'pricing-quiz',
          adv2: quizResult || 'completed',
        });
      }
    };

    window.addEventListener('message', handleQuizCompletion);

    return () => {
      window.removeEventListener('message', handleQuizCompletion);
    };
  }, []);

  const quizId = skipIntro ? QUIZ_SKIPPING_INTRO_ID : QUIZ_WITH_INTRO_ID;
  const quizUrl = `https://quiz.tryinteract.com/#/${quizId}${skipIntro ? '/q/1' : ''}?method=iframe`;

  return (
    <BaseModal
      visible={visible}
      onClose={onClose}
      baseWidth={600}
      hideCloseButton
      key={quizId}
    >
      <Stack
        justify='center'
        align='center'
        background='bg-primary'
        padding={['spacing-xl', 'spacing-md']}
        borderRadius='radius-md'
        overflow='hidden'
        boxSizing='content-box'
      >
        <QuizContainer
          position='relative'
          marginBottom='spacing-md'
        >
          <IFrameContainer>
            <IFrame
              id={`interactApp${quizId}`}
              width='100%'
              height='100%'
              src={quizUrl}
            />
          </IFrameContainer>
        </QuizContainer>
      </Stack>
    </BaseModal>
  );
};

export default PricingQuizModal;
